import { useTranslation } from "react-i18next";
import NoSymbolIcon from "@heroicons/react/24/outline/NoSymbolIcon";
import { useLoad } from "../../components/Load/useLoad";
import LoadFailure from "../../components/Load/LoadFailure";
import { DEFAULT_LANGUAGE, PUBLIC_LANGUAGES } from "../public-parish/paths";
import { useFeatures } from "./FeaturesContext";
import { CLOSED_BAND, CLOSED_MESSAGE } from "./closedScreen";

/** The deploy's default church, by its code (a build setting), read when the page is drawn. */
export function defaultChurchCode() {
  return process.env.REACT_APP_PUBLIC_DEFAULT_CHURCH || "";
}

/**
 * /register while self-registration is closed (the owner's answer 3 of 3 Nov 2026): no sign-in link. It says
 * registration isn't open yet and gives the church's contact - the default church's public e-mail and phone, from its
 * public page, through the public client. A church with no published page has no contact to give, and none is shown; a
 * read that failed says so (standing rule 7). Tagged data-module-off, as every switched-off screen is.
 */
export default function RegistrationClosed() {
  const { t, i18n } = useTranslation();
  const asked = i18n.language?.slice(0, 2);
  const lang = PUBLIC_LANGUAGES.includes(asked) ? asked : DEFAULT_LANGUAGE;
  const code = defaultChurchCode();
  // With the public page off there is no page to read the contact from, and a module that is off is asked nothing.
  const publicPages = useFeatures().isOn("publicPages");
  // The public client is loaded when this is drawn, not with every screen's gate.
  const church = useLoad(() => import("../public-parish/api").then(({ parishRead }) => parishRead(code, "church", { lang })),
    [code, lang, publicPages], { enabled: !!code && publicPages });
  const contact = church.status === "succeeded" && (church.data.email || church.data.phone) ? church.data : null;
  return (
    <div className={CLOSED_BAND} data-module-off="selfRegistration">
      <div className={CLOSED_MESSAGE} data-closed-message>
        <NoSymbolIcon className="h-20 w-20 inline-block opacity-60" />
        <h1 className="text-2xl font-bold mt-4">{t("registrationClosedTitle")}</h1>
        {contact && (
          <div className="mt-4" data-church-contact>
            <p>{t("registrationClosedContact")}</p>
            <p className="font-semibold mt-2">{contact.name}</p>
            {contact.email && <a href={`mailto:${contact.email}`} className="block break-all">{contact.email}</a>}
            {contact.phone && <a href={`tel:${contact.phone.replace(/\s+/g, "")}`} className="block">{contact.phone}</a>}
          </div>
        )}
        {church.status === "failed" && church.problem !== "NOT_FOUND" && (
          <div className="mt-4"><LoadFailure load={church} compact title={t("registrationClosedContactFailed")} /></div>
        )}
      </div>
    </div>
  );
}
